import api from '../../../shared/services/api';
import { API_ENDPOINTS } from '../../../shared/constants/apiEndpoints';

export const prepFileService = {
  /**
   * Fetch all prep files with pagination
   * @returns {Promise<Array>} Array of mapped prep files
   */
  async fetchAll() {
    let allPrepFiles = [];
    let offset = 0;
    const limit = 1000;
    
    do {
      const params = new URLSearchParams();
      params.append('limit', limit.toString());
      params.append('offset', offset.toString());
      
      const response = await api.get(`${API_ENDPOINTS.PREPS.BASE}?${params.toString()}`);
      const data = response.data;
      
      if (data.items && Array.isArray(data.items)) {
        const mappedPrepFiles = data.items.map(item => ({
          ...item,
          id: item.id_,
          filename: item.ligand_name || `prep_${item.id_}`,
          project: item.project || '<none>',
          target: item.target || '<none>',
          batch: item.batch || '<none>',
          ligand_name: item.ligand_name || '<none>',
          prep_workflow: item.prep_workflow || '',
          mutation: item.mutation || '',
          subgroup: item.subgroup || '',
          ligand_tag: item.ligand_tag || '',
          created_at: item.created_at,
          prepared_at: item.prepared_at,
          author: item.author || '<none>',
          notes: item.notes || '',
          origin_name: item.origin?.origin_name || '<none>',
          origin_type: item.origin?.origin_type || '',
          source_path: item.source_path || '',
          tags: item.tags || []
        }));
        
        allPrepFiles = [...allPrepFiles, ...mappedPrepFiles];
        
        if (mappedPrepFiles.length < limit) {
          break;
        }
        
        offset += limit;
      } else {
        break;
      }
    } while (true);
    
    return allPrepFiles;
  },

  /**
   * Update multiple prep files
   * @param {Array} updateRequests - Array of update objects with id_ and fields to update
   * @returns {Promise<Object>} API response
   */
  async updateBulk(updateRequests) {
    return await api.put(API_ENDPOINTS.PREPS.BASE, updateRequests);
  },

  /**
   * Format date value for API
   * @param {string} dateValue - Date in various formats
   * @returns {string} Formatted date string
   */
  formatDateForAPI(dateValue) {
    if (typeof dateValue === 'string') {
      // Check if it's a 6-digit date (YYMMDD)
      if (/^\d{6}$/.test(dateValue)) {
        // Convert YYMMDD to YYYY-MM-DD
        const yy = dateValue.substring(0, 2);
        const mm = dateValue.substring(2, 4);
        const dd = dateValue.substring(4, 6);
        // Assume 20XX for years 00-50, 19XX for years 51-99
        const yyyy = parseInt(yy) <= 50 ? '20' + yy : '19' + yy;
        return `${yyyy}-${mm}-${dd}`;
      }
      // Check if it's an 8-digit date (YYYYMMDD)
      else if (/^\d{8}$/.test(dateValue)) {
        // Convert YYYYMMDD to YYYY-MM-DD
        const yyyy = dateValue.substring(0, 4);
        const mm = dateValue.substring(4, 6);
        const dd = dateValue.substring(6, 8);
        return `${yyyy}-${mm}-${dd}`;
      }
      // Check if it's already in ISO format (YYYY-MM-DD)
      else if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
        return dateValue;
      }
    }
    // For any other format, return as is and let backend handle validation
    return dateValue;
  },

  /**
   * Prepare update requests from preview data
   * @param {Array} previewData - Preview data to convert
   * @param {Array} prepFiles - Original prep files for reference
   * @returns {Array} Formatted update requests
   */
  prepareUpdateRequests(previewData, prepFiles) {
    return previewData.map(item => {
      // Find the original row to get the id_
      const originalRow = prepFiles.find(row => row.id === item.id);
      if (!originalRow) {
        console.error('Could not find original row for id:', item.id);
        return null;
      }
      
      // Only include fields that have values
      const updates = { id_: originalRow.id_ };
      
      Object.keys(item).forEach(key => {
        if (key !== 'id' && item[key] !== undefined && item[key] !== '') {
          // Special handling for prepared_at field
          if (key === 'prepared_at') {
            updates[key] = this.formatDateForAPI(item[key]);
          } else {
            // For non-date fields, keep as is
            updates[key] = item[key];
          }
        }
      });
      
      return updates;
    }).filter(update => update !== null);
  }
};